import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle } from '../comps';
import { Poppins, Spacer } from 'src/common';
import { queryCache } from 'react-query';
import { Guard } from '../Guard';
import TabsMenu from 'src/components/TabsMenu';
import { FromExisting, FromSuggested } from './comps';
import { motion } from 'framer-motion';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AiSvg } from 'src/assets/dynamicSvg';
import { ActiveTab } from './types';
import { useCache } from 'src/graphql';
import { ControlQuery, useUpsertControlsMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';

const StyledModal = styled(Modal)`
  width: 1100px;
  padding: 0;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .content {
    overflow-y: auto;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface SuggestFrameworksModalProps {
  onClose: () => void;
  control: NonNullable<ControlQuery['control']>;
}

export const SuggestFrameworksModal: React.FC<SuggestFrameworksModalProps> = ({ onClose, control }) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('suggested_frameworks');
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [checkedSuggested, setCheckedSuggested] = useState<string | null>(null);
  const [checkedExisting, setCheckedExisting] = useState<string | null>(null);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const { refetchQuery } = useCache();
  const { workspaceId } = useAuth();

  const existingFrameworkControlIds = control?.frameworkControls.map((fc) => fc.id) || [];

  const onSuccess = () => {
    refetchQuery({ include: ['control'] });
    queryCache.invalidateQueries();
    onClose();
  };

  const [upsertControls, { loading: isSubmitting }] = useUpsertControlsMutation({
    onCompleted: onSuccess,
    onError: (error) => setErr(error.message || 'Something went wrong'),
  });

  const handleSubmit = (isSuggested: boolean) => {
    const frameworkControlIds = (isSuggested ? [checkedSuggested] : [checkedExisting]).filter((id) => !!id) as string[];

    upsertControls({
      variables: {
        workspaceId,
        controlsData: [
          {
            id: control.id,
            frameworkControlIds,
          },
        ],
      },
    });
  };

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={!!checkedSuggested || !!checkedExisting}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false}>
          <div className="content">
            <Spacer $px={40} />
            <div className="h-padding">
              <Poppins className="m-title" px={28}>
                Select framework
              </Poppins>
              <ModalSubTitle title={'CONTROL'} value={control.name} />
              <Spacer $px={20} />
              <TabsMenu
                tabs={[
                  {
                    label: (
                      <>
                        <AiSvg style={{ marginRight: 8 }} />
                        <Poppins
                          px={18}
                          weight={600}
                          color={activeTab === 'suggested_frameworks' ? 'prussianBlue' : 'cflowerBlue'}
                        >
                          Suggested Frameworks
                        </Poppins>
                      </>
                    ),
                    value: 'suggested_frameworks',
                    dataCy: 'suggested_frameworks',
                  },
                  { label: 'All Frameworks', value: 'all_frameworks', dataCy: 'all_frameworks' },
                ]}
                activeTab={activeTab}
                setActiveTab={(tab) => {
                  setActiveTab(tab);
                  mpEvent(MPEvents.ButtonClick, {
                    button: tab === 'suggested_frameworks' ? 'Tab:Suggested Frameworks' : 'Tab:All Frameworks',
                    modal: 'Suggest frameworks modal',
                    tags: ['CONTROL', 'RMP', 'FRAMEWORK'],
                  });
                }}
              />
              <Spacer $px={40} />
              {activeTab === 'suggested_frameworks' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                  <FromExisting
                    initialSelectedFrameworks={['ISO 27002']}
                    checked={checkedSuggested}
                    setChecked={setCheckedSuggested}
                    onSubmit={() => handleSubmit(true)}
                    isSubmitting={isSubmitting}
                    existingFrameworkControlIds={existingFrameworkControlIds}
                  />
                </motion.div>
              )}
              {activeTab === 'all_frameworks' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                  <FromExisting
                    selectedFrameworks={selectedFrameworks}
                    setSelectedFrameworks={setSelectedFrameworks}
                    checked={checkedExisting}
                    setChecked={setCheckedExisting}
                    onSubmit={() => handleSubmit(false)}
                    isSubmitting={isSubmitting}
                    existingFrameworkControlIds={existingFrameworkControlIds}
                  />
                </motion.div>
              )}
            </div>
          </div>
          <div className="h-padding">
            {!!err && (
              <>
                <Spacer $px={15} />
                <div className="error">{err}</div>
              </>
            )}
          </div>
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
